// 🔓 / 🔒 for a location — one tap — with "is anyone inside" beside it, and the
// way into the alert grid and the nightly auto-lock. Both shells, on the
// location's own page. Server: Home Automation api/new/security.js.
//
// What the occupancy line means (the rule the server applies):
//   locked < 10 min ago   "checking if anyone is inside" — the 1–10 min window
//   PresenceState = 0     EMPTY — any opening / motion / hand press alerts everyone
//   PresenceState = 1     someone inside — only the 🔒 column of the grid applies
//   no motion sensor      can't tell — nothing alarms on its own
//   motion sensor silent  not heard for 24 h (battery / fault) = as if absent:
//                         can't tell, and it is named in a POPUP — once a day per
//                         location on this device, gone after 5 s (owner, 2026-10-11;
//                         a permanent line took the top of the screen all day)
//
// ⚠️ AND "SILENT" IS NOT ALWAYS THE SENSOR (owner, 2026-10-26). An RF sensor has
// NO network of its own: it is heard by the location's RF receiver (DeviceID 47)
// and by nothing else. Take that receiver down — to fit a better aerial, say —
// and every sensor under it goes quiet in the same second with nothing wrong
// with any of them. The server now says which it is (`rfHub` on
// GetSecurityState, api/new/security.js `rfHubState`), and this screen must
// never tell somebody to check a battery when the receiver is the thing that is
// unplugged. `silenceNotice` is the one place that decision is worded.
//
// Hidden entirely when the server does not answer (backend not deployed, or
// the migration not run) — a customer must not see a broken control.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Typography, CircularProgress, Alert, Snackbar,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import SnoozeIcon from '@mui/icons-material/Snooze';
import { useApp } from '../../core/state/AppContext';
import {
  getSecurityState, setLocationLock, setAutoLock, ackIntrusion,
} from '../../core/api/api';
import { parseServerTime } from '../../core/devices/firmwareInfo';
import SecurityAlertsDialog from './SecurityAlertsDialog';
import AutomationsDialog from './AutomationsDialog';
import { LocationRemindersDialog } from './RfRemindersDialog';
import ReminderSnoozeBar from './ReminderSnoozeBar';
import { FIT_XS } from './fitDialog';

const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
const minsSince = (raw) => {
  const t = parseServerTime(raw);
  return t == null ? null : Math.max(0, Math.floor((Date.now() - t) / 60000));
};

/**
 * The occupancy line for a locked location.
 *
 * `rfHub` is the server's receiver verdict (null on an older server, or when
 * nothing is silent) — the chip names the receiver rather than the sensors when
 * it is the receiver that stopped.
 */
export function occupancyText(loc, rfHub = null) {
  if (!loc || Number(loc.LocationLock) !== 1) return null;
  const m = minsSince(loc.LockedAt);
  if (!Number(loc.MotionSensors)) return { color: 'default', text: 'No motion sensor — can’t tell if anyone is inside' };
  // MotionAlive is absent on an older server — then nothing is known about health.
  if (loc.MotionAlive != null && !Number(loc.MotionAlive)) {
    return rfHub && rfHub.down
      ? { color: 'warning', text: `${rfHub.name || 'RF receiver'} is off — the sensors cannot be heard` }
      : { color: 'warning', text: 'Motion sensor not reporting — can’t tell if anyone is inside' };
  }
  if (!Number(loc.OccupancyEvaluated)) {
    const left = m == null ? null : Math.max(0, 10 - m);
    return { color: 'info', text: `Checking if anyone is inside${left != null ? ` — ${left} min` : ''}` };
  }
  if (loc.PresenceState == null) return { color: 'default', text: 'Can’t tell if anyone is inside' };
  return Number(loc.PresenceState) === 1
    ? { color: 'warning', text: 'Someone is inside' }
    : { color: 'error', text: 'Empty — intrusion alerts ON' };
}

/** "👣 Office Motion (Office) — last heard 6 Oct, 14:20" per silent motion sensor. */
export function deadMotionLines(list) {
  return (list || []).map((s) => {
    const t = parseServerTime(s.LastAt);
    const when = t == null ? 'never heard'
      : `last heard ${new Date(t).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
    const room = s.RoomName ? ` (${String(s.RoomName).trim()})` : '';
    return `👣 ${String(s.Name || 'Motion sensor').trim()}${room} — ${when}`;
  });
}

/**
 * What to say about silent sensors. -> { text, sensors }
 *
 * ⚠️ THE RECEIVER FIRST. "Check the battery" is wrong, and expensively wrong, for
 * the case where one unplugged box has silenced every sensor at once — the
 * customer is sent looking for flat batteries in six sensors that are fine. Only
 * when the receiver is answering is a silent sensor its own fault.
 *
 * ⚠️ AND IT IS ONE LINE (owner, 2026-10-26: "this type of same message is seen on
 * mobile screen which looks ugly — avoid this type of long messages for any
 * reason"). The first version said everything that was true: what hears the
 * sensors, why the batteries are probably fine, and what stops working until it
 * is back. On a phone that is nine lines of orange across half the screen, and
 * nobody reads it. What the reader has to DO is "check the receiver"; the rest
 * belongs in docs/18-security.md, not on their screen.
 *
 * ⚠️ `sensors` false ALSO shortens it. With the receiver down, naming each silent
 * sensor adds a line per sensor to say the same thing the first line said.
 */
export function silenceNotice(state) {
  const hours = (state && state.motionDeadHours) || 24;
  const hub = state && state.rfHub;
  if (hub && hub.down) {
    const name = hub.name || 'RF receiver';
    const since = parseServerTime(hub.lastAt);
    const when = since == null ? '' : ` since ${new Date(since).toLocaleString([], {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })}`;
    return { text: `📡 ${name} off${when} — check its power and Wi-Fi.`, sensors: false };
  }
  return { text: `Not heard for over ${hours} h — check the battery.`, sensors: true };
}

// ── The silent-sensor popup: once a day per location, on this device ─────────
// localStorage, because it is a per-viewer nicety; when storage is blocked it
// simply shows again — never an error.
export const DEAD_POPUP_MS = 5000;
const deadKey = (locationId) => `sli.deadMotionPopup.${locationId}`;
const today = (now = new Date()) => `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;

/** True when the popup has not been shown for this location today. */
export function deadPopupDue(locationId, now = new Date()) {
  try { return localStorage.getItem(deadKey(locationId)) !== today(now); } catch (e) { return true; }
}
export function markDeadPopupShown(locationId, now = new Date()) {
  try { localStorage.setItem(deadKey(locationId), today(now)); } catch (e) { /* storage blocked */ }
}

export default function SecurityBar({ locationId, locationName = '', dense = false }) {
  const { userId } = useApp();
  const [state, setState] = useState(null);     // GetSecurityState reply, or null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [automationsOpen, setAutomationsOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deadPopup, setDeadPopup] = useState(false);

  const load = useCallback(() => {
    if (!locationId) { setState(null); return; }
    getSecurityState(locationId)
      .then((r) => setState(r?.data?.ok ? r.data : null))
      .catch(() => setState(null));
  }, [locationId]);

  useEffect(() => {
    load();
    // The occupancy verdict lands on the server's own clock — keep up with it.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Once a day: marked the moment it opens, so the 30 s poll cannot re-open it.
  const deadCount = (state?.deadMotion || []).length;
  useEffect(() => {
    if (!locationId || deadCount === 0 || !deadPopupDue(locationId)) return;
    markDeadPopupShown(locationId);
    setDeadPopup(true);
  }, [locationId, deadCount]);

  if (!state) return null;
  const loc = state.location || {};
  const locked = Number(loc.LocationLock) === 1;
  const occ = occupancyText(loc, state.rfHub);
  const since = minsSince(loc.LockedAt);

  const toggle = async () => {
    setBusy(true); setError('');
    try {
      const r = await setLocationLock(locationId, !locked, userId);
      if (r?.data?.ok === false) throw new Error(r.data.error);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not change the lock.');
    } finally {
      setBusy(false);
    }
  };

  const saveAuto = async (f, t) => {
    setBusy(true); setError('');
    try {
      await setAutoLock(locationId, f, t, userId);
      setAutoOpen(false);
      load();
    } catch (e) {
      setError('Could not save the schedule.');
    } finally {
      setBusy(false);
    }
  };

  // An unanswered intrusion repeats every minute until someone answers it here.
  const incident = state.incident;
  const acknowledge = async () => {
    setBusy(true);
    try { await ackIntrusion(locationId, userId); load(); } catch (e) { setError('Could not acknowledge.'); } finally { setBusy(false); }
  };

  const dead = deadMotionLines(state.deadMotion);
  const notice = silenceNotice(state);

  const auto = loc.AutoLockFrom && loc.AutoLockTo ? `${hhmm(loc.AutoLockFrom)}–${hhmm(loc.AutoLockTo)}` : '';

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', ...(dense ? { px: 1, py: 0.75 } : {}),
    }}
    >
      <Tooltip title={locked
        ? `Locked${since != null ? ` ${since} min ago` : ''}${loc.LockSource ? ` · by ${loc.LockSource}` : ''} — tap to unlock`
        : 'Unlocked — tap to lock (when everyone leaves, or for the night)'}
      >
        <span>
          <Button
            size="small" variant="contained" color={locked ? 'error' : 'success'}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : (locked ? <LockIcon /> : <LockOpenIcon />)}
            onClick={toggle} disabled={busy}
            sx={{ fontWeight: 800 }}
          >
            {locked ? 'Locked' : 'Unlocked'}
          </Button>
        </span>
      </Tooltip>
      {occ && <Chip size="small" color={occ.color} label={occ.text} />}
      <Button size="small" startIcon={<NotificationsActiveIcon fontSize="small" />} onClick={() => setAlertsOpen(true)}>
        Alerts
      </Button>
      <Button size="small" startIcon={<AutoModeIcon fontSize="small" />} onClick={() => setAutomationsOpen(true)}>
        Automations
      </Button>
      <Button size="small" startIcon={<SnoozeIcon fontSize="small" />} onClick={() => setRemindersOpen(true)}>
        Reminders
      </Button>
      <Button
        size="small" startIcon={<ScheduleIcon fontSize="small" />}
        onClick={() => { setFrom(hhmm(loc.AutoLockFrom)); setTo(hhmm(loc.AutoLockTo)); setAutoOpen(true); }}
      >
        {auto ? `Auto-lock ${auto}` : 'Auto-lock'}
      </Button>
      {error && <Typography variant="caption" color="error">{error}</Typography>}
      <Snackbar
        open={deadPopup && dead.length > 0}
        autoHideDuration={DEAD_POPUP_MS}
        // A click elsewhere must not cut the 5 s short; ✕ still closes it.
        onClose={(e, reason) => { if (reason !== 'clickaway') setDeadPopup(false); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setDeadPopup(false)} sx={{ maxWidth: 560 }}>
          {notice.text}
          {notice.sensors && dead.map((d) => <div key={d}>{d}</div>)}
        </Alert>
      </Snackbar>
      {incident && (
        <Alert
          severity="error" variant="filled" sx={{ width: '100%', py: 0 }}
          action={<Button color="inherit" size="small" variant="outlined" onClick={acknowledge} disabled={busy}>Acknowledge</Button>}
        >
          {`${String(incident.Title || 'Intrusion').replace(/^🚨\s*/, '')} — repeating until someone answers`}
        </Alert>
      )}

      {/* A sensor left open nags one named person until it is shut. The bar
          shows what is nagging ME, with its snooze, right here on the location. */}
      <ReminderSnoozeBar sx={{ width: '100%' }} />
      <LocationRemindersDialog
        open={remindersOpen}
        onClose={() => setRemindersOpen(false)}
        locationId={locationId}
        locationName={locationName || loc.LocationName || ''}
      />

      <AutomationsDialog
        open={automationsOpen}
        onClose={() => setAutomationsOpen(false)}
        locationId={locationId}
        locationName={locationName || loc.LocationName || ''}
      />

      <SecurityAlertsDialog
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        locationId={locationId}
        locationName={locationName || loc.LocationName || ''}
      />

      <Dialog open={autoOpen} onClose={() => setAutoOpen(false)} {...FIT_XS}>
        <DialogTitle sx={{ pb: 0.5 }}>Lock every night</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Locks at the first time and unlocks at the second, every day. It never unlocks a lock someone set by hand,
            and opening the door lock with a card, fingerprint or password unlocks it early.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" type="time" label="Lock at" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="time" label="Unlock at" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
          {from && to && from === to && <Alert severity="warning" sx={{ mt: 1 }}>The two times must differ.</Alert>}
        </DialogContent>
        <DialogActions>
          {auto && <Button color="error" onClick={() => saveAuto('', '')} disabled={busy}>Turn off</Button>}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setAutoOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveAuto(from, to)} disabled={busy || !from || !to || from === to}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
